/**
 * Preferences › Updates: the Production / Preview switch, shown once the
 * version is clicked five times. A switch confirms first, then restarts
 * Denker on the other channel.
 */
import { useState } from "react";
import { ConfirmDialog, PocketRow, SelectField } from "@/components/ui";
import {
  channelName,
  switchReleaseChannel,
} from "@/desktop/release-channel-switch";
import { useT } from "@/i18n";
import { releaseChannelMessages } from "@/i18n/messages/release-channel";
import { releaseChannel, type SwitchableChannel } from "@/lib/release-channel";

const CHANNELS: readonly SwitchableChannel[] = ["production", "preview"];

export function ReleaseChannelRow() {
  const t = useT(releaseChannelMessages);
  const current = releaseChannel();
  const [target, setTarget] = useState<SwitchableChannel | null>(null);
  const [switching, setSwitching] = useState(false);
  if (current !== "production" && current !== "preview") return null;

  const confirm = (channel: SwitchableChannel) => {
    setSwitching(true);
    void switchReleaseChannel(channel).catch(() => {
      setSwitching(false);
      setTarget(null);
    });
  };

  return (
    <>
      <PocketRow
        testId="settings-row-release-channel"
        title={t("releaseChannel")}
        trailing={
          <SelectField
            variant="value"
            value={current}
            options={CHANNELS.map((channel) => ({
              value: channel,
              label: t(channel),
            }))}
            onChange={(next) => {
              const channel = CHANNELS.find((option) => option === next);
              if (channel && channel !== current) setTarget(channel);
            }}
            testId="release-channel-select"
            ariaLabel={t("releaseChannel")}
          />
        }
      />
      {target && (
        <ConfirmDialog
          title={t("switchTitle", { channel: channelName(target) })}
          description={t("switchBody", { channel: channelName(target) })}
          confirmLabel={t("switchAction")}
          variant="default"
          busy={switching}
          onConfirm={() => confirm(target)}
          onCancel={() => {
            if (!switching) setTarget(null);
          }}
          data-testid="release-channel-confirm"
        />
      )}
    </>
  );
}
